import { msg } from '@lingui/core/macro';
import type { MessageDescriptor } from 'shared/i18n';
import { findFontEntry, FontWeightValue } from './font-registry';

/** Все веса «Начертания» по возрастанию. Пункты для шрифта — пересечение со `weightsFor(family)`. */
export const FONT_WEIGHTS: readonly { value: FontWeightValue; label: MessageDescriptor }[] = [
  { value: 300, label: msg`Лёгкий` },
  { value: 400, label: msg`Обычный` },
  { value: 500, label: msg`Средний` },
  { value: 600, label: msg`Полужирный` },
  { value: 700, label: msg`Жирный` },
  { value: 900, label: msg`Чёрный` },
];

/** Шрифт не из набора: о его весах ничего не известно, кроме того, что обычный и жирный есть почти у всех. */
const UNKNOWN_FAMILY_WEIGHTS: FontWeightValue[] = [400, 700];

const KEYWORD_WEIGHTS: Record<string, number> = { normal: 400, bold: 700 };

/** Веса семейства из реестра; для шрифта вне набора — 400 и 700. */
export function weightsFor(family: string | undefined): FontWeightValue[] {
  return findFontEntry(family)?.weights ?? UNKNOWN_FAMILY_WEIGHTS;
}

/**
 * Сохранённое начертание → число. Старые табло хранят ключевые слова: `normal` = 400,
 * `bold` = 700, `lighter` / `bolder` — самый лёгкий / тяжёлый вес семейства (у канвы нет
 * родителя, относительно которого их считать). Непонятное значение читается как 400.
 */
export function normalizeFontWeight(weight: string | number | undefined | null, family?: string): number {
  if (weight === undefined || weight === null || weight === '') return 400;
  if (typeof weight === 'number') return Number.isFinite(weight) ? weight : 400;
  const key = weight.trim().toLowerCase();
  if (key in KEYWORD_WEIGHTS) return KEYWORD_WEIGHTS[key];
  const weights = weightsFor(family);
  if (key === 'lighter') return weights[0];
  if (key === 'bolder') return weights[weights.length - 1];
  const numeric = Number(key);
  return Number.isFinite(numeric) && numeric > 0 ? numeric : 400;
}

/**
 * Ближайший настоящий вес семейства — по правилу подбора начертания CSS Fonts 4 (§5.2): до 400 сначала
 * ищется легче, затем тяжелее; 400–500 — сначала до 500, затем легче, затем тяжелее; больше 500 —
 * сначала тяжелее, затем легче. Иначе браузер синтезирует жирность, и шрифт на канве «плывёт».
 */
export function nearestWeight(family: string | undefined, weight: string | number | undefined | null): FontWeightValue {
  const desired = normalizeFontWeight(weight, family);
  const weights = weightsFor(family);
  if (weights.includes(desired as FontWeightValue)) return desired as FontWeightValue;

  const lighter = weights.filter((w) => w < desired).reverse();
  const heavier = weights.filter((w) => w > desired);
  if (desired < 400) return lighter[0] ?? heavier[0];
  if (desired <= 500) {
    const upTo500 = heavier.filter((w) => w <= 500);
    return upTo500[0] ?? lighter[0] ?? heavier[0];
  }
  return heavier[0] ?? lighter[0];
}

export interface FontWeightChoice {
  value: FontWeightValue | number;
  /** Подпись из `FONT_WEIGHTS`; у нестандартного веса (`800`) её нет — пишется число. */
  label: MessageDescriptor | undefined;
}

/**
 * Пункты «Начертания» для шрифта — только его настоящие веса. Сохранённый вес, которого у шрифта нет
 * (табло со старым `900` у Oswald), добавляется отдельным пунктом: подменять сохранённое молча нельзя.
 */
export function fontWeightChoices(family: string | undefined, current?: string | number | null): FontWeightChoice[] {
  const weights: number[] = [...weightsFor(family)];
  if (current !== undefined && current !== null && current !== '') {
    const saved = normalizeFontWeight(current, family);
    if (!weights.includes(saved)) weights.push(saved);
  }
  return weights
    .sort((a, b) => a - b)
    .map((value) => ({ value, label: FONT_WEIGHTS.find((weight) => weight.value === value)?.label }));
}
